import { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PencilLine, Trash2, X } from "lucide-react";
import SectionSubtypeEditor from "@/components/SectionSubtypeEditor";

/**
 * Bottom-left inline section editor. Shown when a section is selected in the
 * TOC or double-clicked in the preview. Header row exposes level (H1/H2/H3),
 * heading input, hidden switch, delete, and close-back-to-overview.
 */
const BlogEditorInlineSection = forwardRef(function BlogEditorInlineSection(
  {
    section, index, updateSection, setSection,
    confirmDelete, removeSectionWithToast, onClose,
  },
  ref,
) {
  return (
    <div ref={ref}
         data-testid="inline-section-editor"
         className="flex-1 min-h-0 border-t border-slate-200 bg-slate-50/50 flex flex-col">
      <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-2 shrink-0">
        <PencilLine className="w-4 h-4 text-sky-700"/>
        <div className="text-xs uppercase tracking-widest text-slate-500">Editing section</div>
        <Select value={String(section.level)} onValueChange={v => updateSection(index, "level", Number(v))}>
          <SelectTrigger className="w-20 h-8 text-xs"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">H1</SelectItem>
            <SelectItem value="2">H2</SelectItem>
            <SelectItem value="3">H3</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="inline-section-heading"
          value={section.heading}
          onChange={e => updateSection(index, "heading", e.target.value)}
          className="flex-1 h-8 text-sm"
          placeholder="Section heading"
        />
        <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 mr-1">
          <Switch data-testid={`inline-visibility-${section.id}`}
                  checked={!section.hidden}
                  onCheckedChange={(v) => updateSection(index, "hidden", !v)}/>
          {section.hidden ? "Hidden" : "Visible"}
        </label>
        <button
          onClick={() => confirmDelete(
            "Delete section?",
            `"${section.heading || "Untitled section"}" will be removed. You can undo with Ctrl+Z.`,
            () => removeSectionWithToast(section.id, section.heading),
          )}
          className="text-red-500 hover:text-red-700"
          aria-label="Delete section" title="Delete section">
          <Trash2 className="w-4 h-4"/>
        </button>
        <button
          data-testid="inline-section-close"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close editor" title="Back to overview">
          <X className="w-4 h-4"/>
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <SectionSubtypeEditor
          key={section.id}
          section={section}
          onChange={setSection}
          confirmDelete={confirmDelete}
        />
      </div>
    </div>
  );
});

export default BlogEditorInlineSection;
